/**
 * useAlias Hook
 * Provides organization-specific label aliases for valuation forms
 *
 * Usage:
 * const { getLabel, examples } = useAlias();
 * const title = getLabel('sales_value_title'); // Returns alias or default
 */

import { useOrganization } from '@/lib/platform/contexts/organization-context';
import { DEFAULT_LABELS, LABEL_EXAMPLES, type LabelKey } from '@/lib/platform/data/label-defaults';

// Data moved to lib/platform/data/label-defaults.ts (server-safe — also used
// by the AI chat terminology context); re-exported here for hook consumers.
export { DEFAULT_LABELS, LABEL_EXAMPLES, type LabelKey };

interface LabelAliases {
  [key: string]: string | null | undefined;
}

export function useAlias() {
  const { organization } = useOrganization();

  // Get label aliases from organization (with type safety)
  const aliases = (organization?.label_aliases || {}) as LabelAliases;

  /**
   * Get label text - returns alias if set, otherwise returns default
   * @param key - Label key
   * @returns Label text (alias or default)
   */
  const getLabel = (key: LabelKey): string => {
    // Check if there's a custom alias
    const alias = aliases[key];

    // Return alias if it exists and is not empty, otherwise return default
    if (alias && typeof alias === 'string' && alias.trim() !== '') {
      return alias.trim();
    }

    // Return default label
    return DEFAULT_LABELS[key];
  };

  /**
   * Check if a label has been customized
   * @param key - Label key
   * @returns true if customized, false if using default
   */
  const isCustomized = (key: LabelKey): boolean => {
    const alias = aliases[key];
    return !!(alias && typeof alias === 'string' && alias.trim() !== '');
  };

  /**
   * Get all examples for a specific label
   * @param key - Label key
   * @returns Object with examples for each industry
   */
  const getExamples = (key: LabelKey) => {
    return LABEL_EXAMPLES[key];
  };

  return {
    getLabel,
    isCustomized,
    getExamples,
    examples: LABEL_EXAMPLES,
    defaults: DEFAULT_LABELS,
  };
}
